<template>
  <ul class="navbar-nav" v-if="isAuthenticated">
    <!-- Opción de inicio siempre presente -->
    <li class="nav-item" v-if="!hasHomeMenu">
      <router-link class="nav-link" to="/">
        <i class="bi bi-house me-1"></i>Inicio
      </router-link>
    </li>
    
    <!-- Elementos padre con submenús -->
    <li class="nav-item dropdown" v-for="group in menuGroups" :key="group.parentId">
      <a class="nav-link dropdown-toggle" href="#" role="button" data-bs-toggle="dropdown" aria-expanded="false">
        <i v-if="group.parentIcon" :class="'bi bi-' + group.parentIcon + ' me-1'"></i>
        {{ group.parentTitle }}
      </a>
      <ul class="dropdown-menu">
        <li v-for="child in group.children" :key="child.MenuId">
          <router-link class="dropdown-item" :to="getRoutePath(child.Enlace)">
            <i v-if="child.Imagen" :class="'bi bi-' + child.Imagen + ' me-1'"></i>
            {{ child.Titulo }}
          </router-link>
        </li>
      </ul>
    </li>
    
    <!-- Elementos independientes sin hijos -->
    <li class="nav-item" v-for="menuItem in standaloneItems" :key="menuItem.MenuId">
      <router-link class="nav-link" :to="getRoutePath(menuItem.Enlace)">
        <i v-if="menuItem.Imagen" :class="'bi bi-' + menuItem.Imagen + ' me-1'"></i>
        {{ menuItem.Titulo }}
      </router-link>
    </li>
    
  </ul>
</template>

<script>
import authService from '@/shared/services/auth';
import { formatRoute } from '@/shared/utils/route-helper';

export default {
  name: 'DynamicMenu',
  data() {
    return {
      menuItems: [],
      menuGroups: [],
      standaloneItems: []
    };
  },
  computed: {
    isAuthenticated() {
      return authService.isAuthenticated();
    },
    hasHomeMenu() {
      // Verificar si ya existe una opción de inicio en el menú
      return this.menuItems.some(item => item.Enlace === '' || item.Enlace === '/');
    }
  },
  created() {
    this.loadMenu();
    
    // Escuchar eventos de cambio de ruta para recargar el menú si es necesario
    this.$router.beforeEach((to, from, next) => {
      if (to.path === '/login' || from.path === '/login') {
        // Si el usuario acaba de iniciar sesión o cerrar sesión, recargar el menú
        this.$nextTick(() => {
          this.loadMenu();
        });
      }
      next();
    });
    
    // En Vue 3 no podemos usar $on en $root
    // Usaremos un enfoque alternativo para actualizar el menú
    
    // Verificar el menú cada 2 segundos si el usuario está autenticado
    setInterval(() => {
      if (this.isAuthenticated) {
        this.loadMenu();
      }
    }, 2000);
  },
  methods: {
    getRoutePath(enlace) {
      return formatRoute(enlace);
    },
    loadMenu() {
      // Obtener el menú del usuario desde el servicio de autenticación
      const userMenu = authService.getUserMenu();
      
      if (userMenu && Array.isArray(userMenu)) {
        // Filtrar solo elementos activos y que deben estar en el menú superior
        this.menuItems = userMenu.filter(item => item.EsActivo === true && item.EsTop === true);
        
        // Identificar elementos padre (que tienen hijos)
        const parentMenuIds = new Set();
        const parentMenuItems = {};
        
        // Primero identificamos todos los IDs de padres mencionados
        this.menuItems.forEach(item => {
          if (item.MenuIdPadre && item.MenuIdPadre > 0) {
            parentMenuIds.add(item.MenuIdPadre);
          }
        });
        
        // Crear grupos de menú basados en la relación padre-hijo
        this.menuGroups = [];
        this.standaloneItems = [];
        
        // Agrupar elementos por su padre
        const menuItemsByParent = {};
        
        // Inicializar grupos vacíos para cada padre
        parentMenuIds.forEach(parentId => {
          menuItemsByParent[parentId] = [];
        });
        
        // Asignar elementos a sus grupos padre
        this.menuItems.forEach(item => {
          // Si es un elemento con padre y ese padre existe en nuestro conjunto
          if (item.MenuIdPadre && item.MenuIdPadre > 0 && parentMenuIds.has(item.MenuIdPadre)) {
            // Añadir al grupo de su padre
            if (!menuItemsByParent[item.MenuIdPadre]) {
              menuItemsByParent[item.MenuIdPadre] = [];
            }
            menuItemsByParent[item.MenuIdPadre].push(item);
            
            // Guardar referencia al elemento padre si no lo tenemos aún
            if (!parentMenuItems[item.MenuIdPadre]) {
              // Buscar el elemento padre en la lista de menús
              const parentItem = this.menuItems.find(parent => parent.MenuId === item.MenuIdPadre);
              if (parentItem) {
                parentMenuItems[item.MenuIdPadre] = parentItem;
              }
            }
          } 
          // Si es un elemento padre (tiene hijos)
          else if (parentMenuIds.has(item.MenuId)) {
            // Guardar referencia al elemento padre
            parentMenuItems[item.MenuId] = item;
          }
          // Si no es padre ni tiene padre, es un elemento independiente
          else if (!item.MenuIdPadre || item.MenuIdPadre === 0) {
            this.standaloneItems.push(item);
          }
        });
        
        // Crear los grupos finales con título del padre e hijos ordenados
        Object.keys(menuItemsByParent).forEach(parentId => {
          const children = menuItemsByParent[parentId];
          const parentItem = parentMenuItems[parentId];
          
          if (children.length > 0 && parentItem) {
            // Ordenar los hijos por el campo Orden
            const sortedChildren = children.sort((a, b) => {
              const ordenA = parseFloat(a.Orden) || 999;
              const ordenB = parseFloat(b.Orden) || 999;
              return ordenA - ordenB;
            });
            
            this.menuGroups.push({
              parentId: parentId,
              parentTitle: parentItem.Titulo,
              parentIcon: parentItem.Imagen,
              order: parentItem.Orden,
              children: sortedChildren
            });
          }
        });
        
        // Ordenar los grupos por el campo "Orden" del padre
        this.menuGroups.sort((a, b) => {
          const ordenA = parseFloat(a.order) || 999;
          const ordenB = parseFloat(b.order) || 999;
          return ordenA - ordenB;
        });
        
        // Ordenar los elementos independientes por Orden
        this.standaloneItems.sort((a, b) => {
          const ordenA = parseFloat(a.Orden) || 999;
          const ordenB = parseFloat(b.Orden) || 999;
          return ordenA - ordenB;
        });
      } else {
        this.menuItems = [];
        this.menuGroups = [];
        this.standaloneItems = [];
      }
    }
  }
};
</script>
